import { useCallback, useEffect, useRef, useState } from 'react';

/** Time each frame is held during playback. */
const STEP_MS = 450;

/** Extra pause on the newest frame, so the loop reads as a loop. */
const END_DWELL_MS = 1400;

export interface RadarAnimation {
  /** Index into the frame list currently being shown. */
  index: number;
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Jump to a frame. Pauses playback — scrubbing implies manual control. */
  seek: (index: number) => void;
  /** Step one frame in either direction, wrapping at the ends. */
  step: (delta: number) => void;
}

/**
 * Drives which radar frame is on screen.
 *
 * The frame list is a moving window: RainViewer drops the oldest frame and adds
 * a new one every few minutes. When that happens, a viewer parked on the newest
 * frame should follow the new newest (so an idle tab keeps showing live radar),
 * while a viewer who scrubbed back into the past should stay where they are.
 */
export function useRadarAnimation(frameCount: number): RadarAnimation {
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const previousCount = useRef(0);

  // Reconcile the index whenever the window shifts.
  useEffect(() => {
    const previous = previousCount.current;
    previousCount.current = frameCount;

    if (frameCount === 0) {
      setIndex(0);
      return;
    }

    setIndex((current) => {
      const wasAtNewest = previous === 0 || current >= previous - 1;
      return wasAtNewest ? frameCount - 1 : Math.min(current, frameCount - 1);
    });
  }, [frameCount]);

  // Self-rescheduling tick, so the newest frame can dwell longer than the rest.
  useEffect(() => {
    if (!isPlaying || frameCount === 0) return;

    const isNewest = index >= frameCount - 1;
    const timer = setTimeout(
      () => setIndex((current) => (current + 1) % frameCount),
      isNewest ? END_DWELL_MS : STEP_MS,
    );

    return () => clearTimeout(timer);
  }, [isPlaying, index, frameCount]);

  const seek = useCallback(
    (next: number) => {
      setIsPlaying(false);
      setIndex(Math.max(0, Math.min(next, Math.max(frameCount - 1, 0))));
    },
    [frameCount],
  );

  const step = useCallback(
    (delta: number) => {
      if (frameCount === 0) return;
      setIsPlaying(false);
      setIndex((current) => (current + delta + frameCount) % frameCount);
    },
    [frameCount],
  );

  return {
    index,
    isPlaying,
    play: useCallback(() => setIsPlaying(true), []),
    pause: useCallback(() => setIsPlaying(false), []),
    toggle: useCallback(() => setIsPlaying((playing) => !playing), []),
    seek,
    step,
  };
}
